import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import { ErrorModal } from "../../components/ModalAlert";
import { LoaderSpinner } from "../../components/spinner/loader";
import DataTable from "../../components/ui/DataTable";
import RowAction, { RowActions } from "../../components/ui/RowAction";
import { IconEye } from "../../components/ui/icons";
import { formatCurrencyManual } from "../../utils/formatters";
import supabase from "../../supabase";
import SearchIcon from "@material-ui/icons/Search";
import { ticketObjectStatus } from "../../constants/ticketStatus";

const ClientSelection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [balanceFilter, setBalanceFilter] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientBalances, setClientBalances] = useState({});

  useEffect(() => {
    getClients();
  }, []);

  const calculateClientBalances = async (clients) => {
    try {
      const { data: ticketsData, error: ticketsError } = await supabase
        .from("tickets")
        .select(`
          clientId,
          status,
          ticketDetails(
            price,
            quantity,
            ivaPercentage
          )
        `)
        .in("status", [ticketObjectStatus.autorizada, ticketObjectStatus.pagada]);

      if (ticketsError) throw ticketsError;

      const balances = {};
      clients.forEach(client => {
        const clientId = client.clientId;
        const totalFacturasPendientes = ticketsData
          .filter(t => t.clientId === clientId && t.status === ticketObjectStatus.autorizada)
          .reduce((sum, ticket) => {
            const totalAmount = (ticket.ticketDetails || []).reduce((detailSum, detail) => {
              const price = Number(detail.price) || 0;
              const quantity = Number(detail.quantity) || 0;
              const ivaPercentage = Number(detail.ivaPercentage) || 0;
              return detailSum + (price * quantity * (1 + (ivaPercentage / 100)));
            }, 0);
            return sum + totalAmount;
          }, 0);
        const saldoAFavor = Number(client.balance || 0);
        const saldoBase = Math.round((totalFacturasPendientes - saldoAFavor) * 100) / 100;
        balances[clientId] = -saldoBase;
      });
      return balances;
    } catch (error) {
      console.error("Error calculando saldos:", error);
      return {};
    }
  };

  const getClients = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("clients").select("*");

      if (error) throw error;

      setClients(data);

      // Calcular saldos para todos los clientes de una vez
      const balances = await calculateClientBalances(data);
      setClientBalances(balances);
    } catch (error) {
      ErrorModal({
        title: "Error obteniendo los clientes",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "company",
      label: "Cliente",
      options: { filter: false, sort: true },
    },
    {
      name: "cuit",
      label: "CUIT",
      options: { filter: false, sort: true },
    },
    {
      // El saldo se inyecta en la fila (ver `visibleClients`) para que la
      // columna se pueda ordenar de verdad, no sólo mostrar.
      name: "saldo",
      label: "Saldo actual",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { textAlign: "right", whiteSpace: "nowrap" } }),
        setCellHeaderProps: () => ({ style: { textAlign: "right" } }),
        customBodyRender: (value) => {
          const saldo = Number(value) || 0;
          return (
            <Box
              component="span"
              sx={{
                fontVariantNumeric: "tabular-nums",
                fontWeight: saldo < 0 ? 600 : 400,
                color: saldo < 0 ? "var(--sup-state-danger-ink)" : "var(--sup-ink-2)",
              }}
            >
              {formatCurrencyManual(saldo)}
            </Box>
          );
        },
      },
    },
    {
      name: "acciones",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ style: { width: 64, textAlign: "right" } }),
        setCellHeaderProps: () => ({ style: { width: 64, textAlign: "right" } }),
        customBodyRenderLite: (dataIndex) => {
          const client = visibleClients[dataIndex];
          return (
            <RowActions>
              <RowAction
                icon={IconEye}
                title="Ver cuenta corriente"
                onClick={() => navigate("/client-current-account", { state: { client } })}
              />
            </RowActions>
          );
        },
      },
    },
  ];

  const options = {
    responsive: "simple",
    selectableRows: "none",
    // Los desplegables listaban un valor por cliente: no filtraban nada útil.
    filter: false,
    // Arranca por el más deudor: es a quien hay que ir a cobrar.
    sortOrder: { name: "saldo", direction: "asc" },
    rowsPerPage: 25,
    downloadOptions: {
      filename: "cuentas-corrientes.csv",
      separator: ";",
      filterOptions: { useDisplayedColumnsOnly: true, useDisplayedRowsOnly: true },
    },
  };

  const term = search.trim().toLowerCase();
  const withBalance = clients.map((client) => ({
    ...client,
    saldo: clientBalances[client.clientId] || 0,
  }));

  const matchesFilter = (client) => {
    if (balanceFilter === "deuda") return client.saldo < 0;
    if (balanceFilter === "favor") return client.saldo > 0;
    if (balanceFilter === "aldia") return client.saldo === 0;
    return true;
  };

  const visibleClients = withBalance.filter((client) => {
    if (!matchesFilter(client)) return false;
    if (!term) return true;
    return [client.company, client.name, client.lastName, client.cuit]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  });

  const deudores = withBalance.filter((client) => client.saldo < 0);
  const totalDeuda = deudores.reduce((acc, client) => acc + client.saldo, 0);

  const balanceChips = [
    {
      id: "todos",
      label: `Todos (${withBalance.length})`,
      active: balanceFilter === null,
      onClick: () => setBalanceFilter(null),
    },
    {
      id: "deuda",
      label: `Con deuda (${deudores.length})`,
      dot: "#e53935",
      active: balanceFilter === "deuda",
      onClick: () => setBalanceFilter(balanceFilter === "deuda" ? null : "deuda"),
    },
    {
      id: "aldia",
      label: `Al día (${withBalance.filter((c) => c.saldo === 0).length})`,
      dot: "#6fa32e",
      active: balanceFilter === "aldia",
      onClick: () => setBalanceFilter(balanceFilter === "aldia" ? null : "aldia"),
    },
    {
      id: "favor",
      label: `Saldo a favor (${withBalance.filter((c) => c.saldo > 0).length})`,
      dot: "#23968a",
      active: balanceFilter === "favor",
      onClick: () => setBalanceFilter(balanceFilter === "favor" ? null : "favor"),
    },
  ];

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box sx={{ width: "100%" }}>
      <DataTable
        data={visibleClients}
        columns={columns}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Buscar por cliente o CUIT…",
        }}
        chips={balanceChips}
        actions={
          deudores.length > 0 && (
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "baseline",
                gap: "8px",
                paddingLeft: "12px",
                borderLeft: "1px solid var(--sup-border)",
              }}
            >
              <Box component="span" sx={{ fontSize: "12px", color: "var(--sup-ink-3)" }}>
                Deuda total
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--sup-state-danger-ink)",
                }}
              >
                {formatCurrencyManual(totalDeuda)}
              </Box>
            </Box>
          )
        }
        resultCount={visibleClients.length}
        totalCount={withBalance.length}
        emptyMessage="No se encontraron clientes"
        options={options}
      />
    </Box>
  );
};

export default ClientSelection;
